import { FilterType, FilterCondition, APIFilterParams, TableFilter } from "../types";
import { StoredFilterConfig } from "../contexts/datatableSlice";
import FilterTextInput from "./FilterTextInput";
import FilterDropdownInput from "./FilterDropdownInput";

/** Filter config type that covers both header filters and programmatic filters */
type FilterConfigProp = TableFilter | StoredFilterConfig | undefined;

interface FilterInputProps {
  filter: FilterCondition;
  headerFilter: FilterConfigProp;
  fieldLabel: string;
  selectedOperator: string;
  onFilterChange: (field: keyof FilterCondition, value: string | string[] | "AND" | "OR") => void;
}

const FilterInput = ({
  filter,
  headerFilter,
  fieldLabel,
  selectedOperator,
  onFilterChange,
}: FilterInputProps) => {
  if (!headerFilter) return null;

  // For text filters, use our custom FilterTextInput component
  if (headerFilter.filterType === FilterType.TEXT) {
    return (
      <FilterTextInput
        value={filter.value as string}
        onChange={(value: unknown) => {
          onFilterChange("value", value as string | string[]);
        }}
        placeHolder={headerFilter.placeHolder}
        filterMode={headerFilter.filterMode}
        selectedOperator={selectedOperator}
        allowFractional={headerFilter.allowFractional}
      />
    );
  }

  // For dropdown filters, use our custom FilterDropdownInput component
  if (headerFilter.filterType === FilterType.DROPDOWN) {
    return (
      <FilterDropdownInput
        label={headerFilter.label || fieldLabel}
        value={Array.isArray(filter.value) ? filter.value : []}
        onChange={(value: string[] | string) => {
          onFilterChange("value", value);
        }}
        options={headerFilter.options || []}
        multiSelect={true}
      />
    );
  }

  // For dropdown_single filters, use FilterDropdownInput in single-select mode
  if (headerFilter.filterType === FilterType.DROPDOWN_SINGLE) {
    return (
      <FilterDropdownInput
        label={headerFilter.label || fieldLabel}
        value={filter.value as string}
        onChange={(value: string[] | string) => {
          onFilterChange("value", value);
        }}
        options={headerFilter.options || []}
        multiSelect={false}
      />
    );
  }

  // For api_dropdown filters, use FilterDropdownInput with API functionality
  if (headerFilter.filterType === FilterType.API_DROPDOWN) {
    // Wrap apiFunction to apply extractorFunction for transforming API response
    const wrappedApiFunction = async (params: {
      prefix?: string;
      page?: number;
      size?: number;
    }) => {
      const apiParams: APIFilterParams = {
        prefix: params.prefix ?? "",
        page: params.page ?? 1,
        size: params.size || headerFilter.pageSize || 20,
      };
      const response = await headerFilter.apiFunction?.(apiParams);
      const extractedItems = headerFilter.extractorFunction?.(response) || [];

      // Convert extracted items to the format FilterDropdownInput expects
      const items = extractedItems.map(item => ({
        label: typeof item.label === "string" ? item.label : String(item.value),
        value: item.value,
      }));

      const resp = response as { pages?: number; total_pages?: number; total?: number };
      return {
        items,
        pages: resp?.total_pages || resp?.pages || 1,
        total: resp?.total || items.length,
      };
    };

    return (
      <FilterDropdownInput
        label={headerFilter.label || fieldLabel}
        value={Array.isArray(filter.value) ? filter.value : []}
        onChange={(value: string[] | string) => {
          onFilterChange("value", value);
        }}
        options={headerFilter.options || []}
        multiSelect={true}
        apiFunction={wrappedApiFunction}
        seeMore={headerFilter.seeMore}
        pageSize={headerFilter.pageSize}
      />
    );
  }

  // For multiDropdown filters, use FilterDropdownInput with hierarchical support
  if (headerFilter.filterType === FilterType.MULTI_DROPDOWN) {
    return (
      <FilterDropdownInput
        label={headerFilter.label || fieldLabel}
        value={Array.isArray(filter.value) ? filter.value : []}
        onChange={(value: string[] | string) => {
          onFilterChange("value", value);
        }}
        options={
          headerFilter.options?.map(o => ({ ...o, children: o.children || o.subComponent })) || []
        }
        multiSelect={true}
      />
    );
  }

  return null;
};

export default FilterInput;
